/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { CdsDisclosure, type Props$1 as CdsDisclosureProps } from '@coursera-web/cds-labs';

import { Skeleton, Typography2 } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import type { CourseItem, Product, WorkflowType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { CoachBrandLabel, CoachBrandLabelWithBeta } from 'bundles/ai-coach-platform/components/branding/CoachBranding';
import { isCoachBrandUpdateEnabled } from 'bundles/ai-coach-platform/utils/epicFeatureFlags';

import _t from 'i18n!nls/ai-coach-platform';

import ShowMoreDisclosureButtons from './ShowMoreDisclosureButtons';

const BrandUpdateDisclosureHeader = () => (
  <>
    <AIGenerateBrandedNewIcon size="medium" />
    <Typography2 component="p" variant="bodyPrimary">
      {_t('Dive deeper on this topic')}
    </Typography2>
  </>
);

const CoachDisclosureHeader = ({ showBetaTag }: { showBetaTag: boolean }) =>
  showBetaTag ? <CoachBrandLabelWithBeta /> : <CoachBrandLabel />;

const styles = {
  skeleton: css`
    /* override default CDS Skeleton styles */
    .cds-Skeleton-root {
      background-color: var(--cds-color-emphasis-primary-background-weak);
    }
  `,
  disclosureHeader: css`
    display: flex;
    gap: 8px;
    align-items: center;
  `,
};

const DisclosureSkeleton = () => {
  return (
    <div className="coach-disclosure-skeleton" css={styles.skeleton}>
      <Skeleton variant="text" height={24} />
      <Skeleton variant="text" width="50%" height={24} />

      <div
        css={css`
          display: flex;
          gap: 8px;
          margin: var(--cds-spacing-200) 0;
        `}
      >
        <Skeleton
          variant="rect"
          width={120}
          height={32} // match the Coach trigger buttons
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
        <Skeleton
          variant="rect"
          width={120}
          height={32} // match the Coach trigger buttons
          css={css`
            border-radius: var(--cds-border-radius-100);
          `}
        />
      </div>
    </div>
  );
};

export type Props = Omit<CdsDisclosureProps, 'header' | 'aria-label'> & {
  'aria-label'?: string;

  /**
   * List of buttons to render within the Disclosure footer area.
   *
   * When the brand update is active, action buttons should not display the sparkle icon
   * in the CoachDisclosure. Pass `showSparkleIcon={false}` to `ExternalCoachAction` when
   * constructing these buttons.
   */
  actionButtons?: React.ReactNode[];

  /**
   * Custom footer to render for the Disclosure footer.
   */
  customFooter?: React.ReactNode;

  /**
   * Shows "Beta" next to the Coach header logo
   */
  showBetaTag?: boolean;
  /**
   * Shows a skeleton version of the disclosure
   * @default false
   */
  loading?: boolean;

  additionalStyles?: SerializedStyles;

  mode: string;
  itemId?: string;
  courseId?: string;
  courseBuilderWorkflowId?: string;
  courseBuilderWorkflowType?: { workflowType: WorkflowType };

  /**
   * Opt-in to the brand update variant. The variant is enabled when this is true AND the
   * `enableBrandUpdate` EPIC flag is active. Replaces the Coach logo header with a sparkle
   * icon and generic label, and updates the aria-label accordingly.
   * @default false
   */
  enableBrandUpdate?: boolean;
};

/**
 * Simple wrapper around CDS Disclosure for Coach-specific use cases.
 */
export const CoachDisclosure = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const {
    loading = false,
    showBetaTag = false,
    enableBrandUpdate = false,
    actionButtons,
    customFooter,
    'aria-label': ariaLabelProp,
    children,
    onToggle,
    additionalStyles,
    mode,
    itemId,
    courseId,
    courseBuilderWorkflowId,
    courseBuilderWorkflowType,
    defaultExpanded = true,
    ...rest
  } = props;

  const isBrandUpdateEnabled = enableBrandUpdate && isCoachBrandUpdateEnabled();
  const ariaLabel =
    ariaLabelProp ??
    (isBrandUpdateEnabled
      ? _t('Coursera AI')
      : _t('#{coachLabel}, AI powered assistant, beta', { coachLabel: 'Coach' }));

  const track = useTracker();

  const showFooter = loading ? false : !!actionButtons || !!customFooter;

  const sharedEventingData = {
    courseItem: {
      id: itemId as string,
    } as CourseItem,
    product: {
      type: 'course',
      id: courseId as string,
    } as Product,
  };

  const disclosureVisibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach',
    {
      coachViewType: 'view_coach_disclosure',
      coachWidgetState: defaultExpanded ? 'open' : 'closed',
      coach: {
        mode,
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
      ...(courseBuilderWorkflowId && { courseBuilderWorkflowId }),
      ...(courseBuilderWorkflowType && { courseBuilderWorkflowType }),
    }
  );

  const handleToggle = (expanded: boolean) => {
    onToggle?.(expanded);

    track('interact_coach', {
      coachInteractionType: expanded ? 'coach_disclosure_expanded' : 'coach_disclosure_collapsed',
      coach: {
        mode,
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
      ...(courseBuilderWorkflowId && { courseBuilderWorkflowId }),
      ...(courseBuilderWorkflowType && { courseBuilderWorkflowType }),
    });
  };

  const footerContent =
    customFooter ?? (actionButtons ? <ShowMoreDisclosureButtons>{actionButtons}</ShowMoreDisclosureButtons> : null);

  return (
    <div ref={disclosureVisibilityTrackingRef}>
      <CdsDisclosure
        {...rest}
        defaultExpanded={defaultExpanded}
        ref={ref}
        aria-label={ariaLabel}
        enableBrandUpdate={isBrandUpdateEnabled}
        header={
          <div css={styles.disclosureHeader}>
            {isBrandUpdateEnabled ? (
              <BrandUpdateDisclosureHeader />
            ) : (
              <CoachDisclosureHeader showBetaTag={showBetaTag} />
            )}
          </div>
        }
        footer={showFooter && footerContent}
        onToggle={handleToggle}
        css={additionalStyles}
      >
        {loading && <DisclosureSkeleton />}
        {!loading && children}
      </CdsDisclosure>
    </div>
  );
});

export default CoachDisclosure;
